import {
  LogIn, LogOut, Mic, MicOff, Volume2, VolumeX, Video, VideoOff,
  ScreenShare, ScreenShareOff, Music, Smile, Hand, Coffee, Settings2, Activity,
  Phone, PhoneOff, BarChart3,
} from 'lucide-react'
import type { ActivityEvent } from '../../services/historyTypes'

// English-only product strings (os-core rule §4.8). Returns the full
// sentence including the actor name.
export function activityText(e: ActivityEvent): string {
  const who = e.actorName || 'Someone'
  switch (e.type) {
    case 'join': return `${who} joined`
    case 'phone_join': return `${who} joined by phone`
    case 'leave': return `${who} left`
    case 'phone_leave': return `${who} left (phone)`
    case 'mute': return `${who} muted`
    case 'unmute': return `${who} unmuted`
    case 'deafen': return `${who} deafened`
    case 'undeafen': return `${who} undeafened`
    case 'camera_on': return `${who} turned camera on`
    case 'camera_off': return `${who} turned camera off`
    case 'screenshare_on': return `${who} started screen share`
    case 'screenshare_off': return `${who} stopped screen share`
    case 'soundboard': return `${who} played ${typeof e.meta?.name === 'string' ? `"${e.meta.name}"` : 'a sound'}`
    case 'reaction': return `${who} reacted ${typeof e.meta?.emoji === 'string' ? e.meta.emoji : ''}`.trimEnd()
    case 'hand_raise': return `${who} raised hand`
    case 'hand_lower': return `${who} lowered hand`
    case 'away_on': return `${who} stepped away`
    case 'away_off': return `${who} is back`
    case 'device_mic': return `${who} switched microphone`
    case 'device_output': return `${who} switched audio output`
    case 'device_camera': return `${who} switched camera`
    // OSPROD-262: logged server-side at poll-run creation (os-spaces
    // polls.ts) — meta.title is the poll set's title.
    case 'poll_start': return `${who} started a poll${typeof e.meta?.title === 'string' ? `: "${e.meta.title}"` : ''}`
    default: return `${who}: ${e.type}`
  }
}

// Leading Lucide glyph + a tailwind colour class per event type.
export function activityIcon(type: string): { Icon: typeof Activity; cls: string } {
  switch (type) {
    case 'join': return { Icon: LogIn, cls: 'text-emerald-400' }
    case 'phone_join': return { Icon: Phone, cls: 'text-emerald-400' }
    case 'leave': return { Icon: LogOut, cls: 'text-muted-foreground' }
    case 'phone_leave': return { Icon: PhoneOff, cls: 'text-muted-foreground' }
    case 'mute': return { Icon: MicOff, cls: 'text-rose-400' }
    case 'unmute': return { Icon: Mic, cls: 'text-emerald-400' }
    case 'deafen': return { Icon: VolumeX, cls: 'text-rose-400' }
    case 'undeafen': return { Icon: Volume2, cls: 'text-emerald-400' }
    case 'camera_on': return { Icon: Video, cls: 'text-sky-400' }
    case 'camera_off': return { Icon: VideoOff, cls: 'text-muted-foreground' }
    case 'screenshare_on': return { Icon: ScreenShare, cls: 'text-sky-400' }
    case 'screenshare_off': return { Icon: ScreenShareOff, cls: 'text-muted-foreground' }
    case 'soundboard': return { Icon: Music, cls: 'text-violet-400' }
    case 'reaction': return { Icon: Smile, cls: 'text-amber-400' }
    case 'hand_raise':
    case 'hand_lower': return { Icon: Hand, cls: 'text-amber-400' }
    case 'away_on':
    case 'away_off': return { Icon: Coffee, cls: 'text-muted-foreground' }
    case 'device_mic':
    case 'device_output':
    case 'device_camera': return { Icon: Settings2, cls: 'text-muted-foreground' }
    case 'poll_start': return { Icon: BarChart3, cls: 'text-violet-400' }
    default: return { Icon: Activity, cls: 'text-muted-foreground' }
  }
}

export function activityClock(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// HH:MM:SS — second precision for the per-row timestamp + hover title.
export function activityClockFull(ts: number): string {
  const d = new Date(ts)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

// The action phrase WITHOUT the actor name, so the feed can render the
// name bold and the verb muted. Kept in sync with activityText.
export function activityVerb(e: ActivityEvent): string {
  switch (e.type) {
    case 'join': return 'joined'
    case 'phone_join': return 'joined by phone'
    case 'leave': return typeof e.meta?.reason === 'string' ? `left (${e.meta.reason})` : 'left'
    case 'phone_leave': return 'left (phone)'
    case 'mute': return 'muted'
    case 'unmute': return 'unmuted'
    case 'deafen': return 'deafened'
    case 'undeafen': return 'undeafened'
    case 'camera_on': return 'turned camera on'
    case 'camera_off': return 'turned camera off'
    case 'screenshare_on': return 'started screen share'
    case 'screenshare_off': return 'stopped screen share'
    case 'soundboard': return `played ${typeof e.meta?.name === 'string' ? `"${e.meta.name}"` : 'a sound'}`
    case 'reaction': return `reacted ${typeof e.meta?.emoji === 'string' ? e.meta.emoji : ''}`.trimEnd()
    case 'hand_raise': return 'raised hand'
    case 'hand_lower': return 'lowered hand'
    case 'away_on': return 'stepped away'
    case 'away_off': return 'is back'
    case 'device_mic': return 'switched microphone'
    case 'device_output': return 'switched audio output'
    case 'device_camera': return 'switched camera'
    case 'poll_start': return `started a poll${typeof e.meta?.title === 'string' ? `: "${e.meta.title}"` : ''}`
    default: return e.type
  }
}

export interface ActivityMinuteGroup { key: string; label: string; items: ActivityEvent[] }

// Bucket an already-chronological event list into contiguous per-minute
// groups so the feed can render a time header per minute. Events are
// produced/stored in ts order, so a single linear pass is enough.
export function groupActivityByMinute(events: ActivityEvent[]): ActivityMinuteGroup[] {
  const groups: ActivityMinuteGroup[] = []
  for (const e of events) {
    const d = new Date(e.ts)
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`
    const last = groups[groups.length - 1]
    if (last && last.key === key) last.items.push(e)
    else groups.push({ key, label: activityClock(e.ts), items: [e] })
  }
  return groups
}
